import type {
  CatalogTemplate,
  CreditPackageResponse,
  GenerationResponse,
  PaymentMethod,
} from "@nura/contracts";
import { useEffect, useState, type ChangeEvent } from "react";
import { TemplateCard, TemplateImage, creditWord } from "../../entities/template";
import {
  BackButton,
  BalanceButton,
  Brand,
  Button,
  Icon,
  ScreenHeader,
} from "../../shared/ui";

export type PrimarySection = "catalog" | "works" | "profile";

const defaultRequirements = [
  { title: "Один человек", description: "Без компании в кадре" },
  { title: "Лицо видно", description: "Без резких теней" },
  { title: "Без фильтров", description: "Исходные цвета фото" },
];

const requirementIcons = ["person", "image", "spark"] as const;

function BottomNavigation({ active, onNavigate }: { active: PrimarySection; onNavigate(section: PrimarySection): void }) {
  const items = [
    { id: "catalog" as const, label: "Образы", icon: "spark" as const },
    { id: "works" as const, label: "Мои работы", icon: "works" as const },
    { id: "profile" as const, label: "Профиль", icon: "person" as const },
  ];
  return (
    <nav className="bottom-nav" aria-label="Основная навигация">
      {items.map((item) => (
        <button
          aria-current={active === item.id ? "page" : undefined}
          className={active === item.id ? "bottom-nav__item bottom-nav__item--active" : "bottom-nav__item"}
          key={item.id}
          onClick={() => onNavigate(item.id)}
          type="button"
        >
          <Icon name={item.icon} />
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

function PageState({ title, copy, action, onAction }: { title: string; copy: string; action?: string; onAction?(): void }) {
  return (
    <section className="page-state" aria-live="polite">
      <div className="page-state__mark" aria-hidden="true"><Icon name="spark" size={26} /></div>
      <h2>{title}</h2>
      <p>{copy}</p>
      {action !== undefined && onAction !== undefined ? <Button onClick={onAction}>{action}</Button> : null}
    </section>
  );
}

export function CatalogScreen({
  templates,
  balance,
  status,
  onOpenTemplate,
  onNavigate,
  onOpenBalance,
  onRetry,
}: {
  templates: CatalogTemplate[];
  balance?: number;
  status: "loading" | "ready" | "error";
  onOpenTemplate(template: CatalogTemplate): void;
  onNavigate(section: PrimarySection): void;
  onOpenBalance(): void;
  onRetry(): void;
}) {
  return (
    <main className="screen catalog-screen" aria-label="Каталог образов">
      <header className="catalog-header">
        <Brand />
        <BalanceButton balance={balance} onClick={onOpenBalance} />
      </header>

      <section className="catalog-hero">
        <span className="eyebrow">Портреты Nura</span>
        <h1>Выберите новый образ</h1>
        <p>Стиль, свет и фон уже настроены</p>
        <span className="folk-sprig" aria-hidden="true"><i /><i /><i /></span>
      </section>

      <section className="catalog-content" aria-busy={status === "loading"} aria-live="polite">
        {status === "loading" ? (
          <div className="catalog-skeleton" aria-label="Загружаем каталог"><span /><span /><span /></div>
        ) : status === "error" ? (
          <PageState
            action="Повторить"
            copy="Проверьте соединение и попробуйте ещё раз. Фото никуда не отправлялись."
            onAction={onRetry}
            title="Каталог не загрузился"
          />
        ) : templates.length === 0 ? (
          <PageState copy="Здесь появятся опубликованные и проверенные образы." title="Первый образ скоро появится" />
        ) : (
          <div className="template-grid">
            {templates.map((template, index) => (
              <TemplateCard
                eager={index === 0}
                key={template.versionId}
                onSelect={onOpenTemplate}
                template={template}
              />
            ))}
          </div>
        )}
      </section>
      <BottomNavigation active="catalog" onNavigate={onNavigate} />
    </main>
  );
}

export function TemplateDetailScreen({
  template,
  onBack,
  onChoosePhoto,
}: {
  template: CatalogTemplate;
  onBack(): void;
  onChoosePhoto(): void;
}) {
  const requirements = template.requirements ?? defaultRequirements;
  return (
    <main className="screen detail-screen" aria-label={`Образ «${template.name}»`}>
      <section className="detail-hero">
        <TemplateImage className="detail-hero__image" eager full template={template} />
        <div className="detail-hero__top">
          <BackButton label="Вернуться в каталог" onClick={onBack} />
        </div>
      </section>

      <section className="detail-copy">
        <span className="detail-kicker"><Icon name="check" size={16} />Проверенный образ</span>
        <h1>{template.name}</h1>
        <p>{template.description}</p>
        <div className="detail-offer">
          <strong className="detail-price">{template.costCredits} {creditWord(template.costCredits)}</strong>
          <span>Спишем только после готового результата. Запуск подтвердите отдельно.</span>
        </div>
      </section>

      {(template.examples?.length ?? 0) > 0 ? (
        <section className="examples-section" aria-labelledby="examples-title">
          <div className="section-intro">
            <h2 id="examples-title">До и после</h2>
            <p>Реальная проверка этого образа</p>
          </div>
          {template.examples!.map((example, index) => (
            <div className="before-after" key={`${example.result.displayUrl}-${index}`}>
              <figure><img alt={`Исходное фото, пример ${index + 1}`} src={example.source.previewUrl} /><figcaption>До</figcaption></figure>
              <figure><img alt={`Результат, пример ${index + 1}`} src={example.result.previewUrl} /><figcaption>После</figcaption></figure>
            </div>
          ))}
          <p className="example-disclaimer">Пример показывает характер света и стилизации. Итог зависит от исходного фото.</p>
        </section>
      ) : null}

      <section className="requirements" aria-labelledby="requirements-title">
        <div className="section-intro">
          <h2 id="requirements-title">Какое фото подойдёт</h2>
          <p>Три ориентира для более точного результата</p>
        </div>
        <div className="requirements-list">
          {requirements.map((requirement, index) => (
            <article className="requirement" key={requirement.title}>
              <span className="requirement__icon"><Icon name={requirementIcons[index % requirementIcons.length]!} /></span>
              <span><strong>{requirement.title}</strong><small>{requirement.description}</small></span>
            </article>
          ))}
        </div>
      </section>

      <div className="bottom-action detail-action">
        <Button onClick={onChoosePhoto} tail={<><span>{template.costCredits} {creditWord(template.costCredits)}</span><Icon name="arrow-right" /></>}>
          Выбрать фото
        </Button>
      </div>
    </main>
  );
}

export function UploadScreen({
  template,
  balance,
  photoUrl,
  error,
  onBack,
  onOpenBalance,
  onSelectPhoto,
  onContinue,
}: {
  template: CatalogTemplate;
  balance?: number;
  photoUrl?: string;
  error?: string;
  onBack(): void;
  onOpenBalance?(): void;
  onSelectPhoto(file: File): void;
  onContinue(): void;
}) {
  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (file !== undefined) onSelectPhoto(file);
    event.currentTarget.value = "";
  }
  return (
    <main className="screen upload-screen" aria-label="Выбор фотографии">
      <ScreenHeader balance={balance} onBack={onBack} onOpenBalance={onOpenBalance} title="Фото для образа" />
      <section className="upload-intro">
        <span className="eyebrow">{template.name}</span>
        <h1>Добавьте своё фото</h1>
        <p>Лучше всего подойдёт портрет анфас при хорошем свете</p>
      </section>

      <label className={photoUrl === undefined ? "photo-picker" : "photo-picker photo-picker--selected"}>
        <input accept="image/jpeg,image/png,image/webp" onChange={handleFile} type="file" />
        {photoUrl === undefined ? (
          <span className="photo-picker__empty">
            <span className="photo-picker__mark" aria-hidden="true"><Icon name="image" size={30} /></span>
            <strong>Выберите портрет</strong>
            <span>Нажмите, чтобы открыть галерею</span>
            <small>JPG, PNG или WebP · до 15 МБ</small>
          </span>
        ) : (
          <><img alt="Выбранная фотография" src={photoUrl} /><span className="selected-photo"><Icon name="image" />Заменить фото</span></>
        )}
      </label>
      {error !== undefined ? <div className="status-banner status-banner--error" role="alert">{error}</div> : null}

      <section className="photo-guidance" aria-label="Требования к фотографии">
        <h2>Перед выбором</h2>
        <div>
          {(template.requirements ?? defaultRequirements).slice(0, 3).map((item) => (
            <span key={item.title}><Icon name="check" size={17} /><strong>{item.title}</strong></span>
          ))}
        </div>
      </section>
      <p className="privacy-note"><Icon name="lock" />Фото используется только для создания выбранного образа</p>

      {photoUrl !== undefined ? <div className="bottom-action upload-action"><Button onClick={onContinue}>Продолжить</Button></div> : null}
    </main>
  );
}

export function ConfirmationScreen({
  template,
  balance,
  photoUrl,
  submitting,
  error,
  onBack,
  onChangePhoto,
  onConfirm,
}: {
  template: CatalogTemplate;
  balance?: number;
  photoUrl: string;
  submitting: boolean;
  error?: string;
  onBack(): void;
  onChangePhoto(): void;
  onConfirm(): void;
}) {
  const enoughCredits = balance === undefined || balance >= template.costCredits;
  return (
    <main className="screen confirmation-screen" aria-label="Подтверждение создания">
      <ScreenHeader onBack={onBack} title="Подтверждение" />
      <section className="confirmation-title"><span className="eyebrow">{template.name}</span><h1>Проверьте перед запуском</h1><p>Убедитесь, что выбраны нужные фото и образ.</p></section>
      <section className="confirmation-overview">
        <img alt="Выбранная фотография" src={photoUrl} />
        <div><span><Icon name="check" size={17} />Фото выбрано</span><strong>{template.name}</strong><button className="text-action" onClick={onChangePhoto} type="button">Изменить фото</button></div>
      </section>
      <dl className="price-summary">
        <div><dt><Icon name="spark" />Образ</dt><dd>{template.name}</dd></div>
        <div><dt><Icon name="coins" />Стоимость</dt><dd>{template.costCredits} {creditWord(template.costCredits)}</dd></div>
        <div><dt><Icon name="works" />Баланс после запуска</dt><dd>{balance === undefined ? "—" : Math.max(0, balance - template.costCredits)} {balance === 1 + template.costCredits ? "кредит" : "кредита"}</dd></div>
      </dl>
      <p className="retention-note"><Icon name="lock" />Исходное фото хранится до 30 дней</p>
      {error !== undefined ? <div className="status-banner status-banner--error" role="alert">{error}</div> : null}
      {!enoughCredits ? <div className="status-banner status-banner--warning">Недостаточно кредитов. Сначала пополните баланс.</div> : null}
      <div className="confirmation-actions">
        <Button disabled={submitting} onClick={onConfirm}>
          {submitting ? "Запускаем…" : `Создать образ · ${template.costCredits} ${creditWord(template.costCredits)}`}
        </Button>
      </div>
    </main>
  );
}

export function ProgressScreen({ template, photoUrl, onOpenWorks }: { template: CatalogTemplate; photoUrl: string; onOpenWorks(): void }) {
  return (
    <main className="screen progress-screen" aria-label="Создание образа" aria-live="polite">
      <header className="progress-header"><Brand /><span className="progress-status"><i />В работе</span></header>
      <section className="progress-title"><span className="eyebrow">{template.name}</span><h1>Создаём ваш образ</h1></section>
      <div className="progress-portrait"><img alt={`Создаётся образ «${template.name}»`} src={photoUrl} /><span aria-hidden="true" /></div>
      <ol className="progress-steps">
        <li className="progress-step progress-step--done"><span><Icon name="check" /></span><div><strong>Проверяем фото</strong><small>Фото загружено и готово</small></div></li>
        <li className="progress-step progress-step--active"><span /><div><strong>Создаём образ</strong><small>Работаем над вашим образом</small></div></li>
        <li className="progress-step"><span /><div><strong>Сохраняем результат</strong><small>Почти готово</small></div></li>
      </ol>
      <aside className="close-notice"><Icon name="works" size={34} /><div><strong>Можно закрыть приложение</strong><p>Бот сообщит, когда результат будет готов</p></div></aside>
      <Button onClick={onOpenWorks} tail={<Icon name="arrow-right" />} variant="secondary">Мои работы</Button>
    </main>
  );
}

export function ResultScreen({
  template,
  resultUrl,
  onBack,
  onSave,
  onAnotherTemplate,
  onReportProblem,
}: {
  template: CatalogTemplate;
  resultUrl: string;
  onBack(): void;
  onSave(): void;
  onAnotherTemplate(): void;
  onReportProblem?(): void;
}) {
  return (
    <main className="screen result-screen" aria-label="Готовый результат">
      <header><BackButton label="Вернуться назад" onClick={onBack} /><span className="result-status"><Icon name="check" size={17} />Готово</span></header>
      <section className="result-title"><span className="eyebrow">{template.name}</span><h1>Ваш новый образ</h1></section>
      <div className="result-image"><img alt={`Готовый результат для образа «${template.name}»`} src={resultUrl} /></div>
      <section className="avatar-preview"><img alt="Круглая обрезка результата" src={resultUrl} /><div><strong>Круглая обрезка</strong><p>Так будет выглядеть аватарка</p></div></section>
      <div className="result-actions">
        <Button onClick={onSave}>Сохранить</Button>
        <Button onClick={onAnotherTemplate} variant="secondary">Другой образ</Button>
        {onReportProblem !== undefined ? <button className="text-action" onClick={onReportProblem} type="button">Сообщить о проблеме</button> : null}
      </div>
    </main>
  );
}

export function WorksScreen({
  history,
  templates,
  previewUrls,
  onNavigate,
  onSave,
}: {
  history: GenerationResponse[];
  templates: CatalogTemplate[];
  previewUrls: Readonly<Record<string, string>>;
  onNavigate(section: PrimarySection): void;
  onSave(item: GenerationResponse): void;
}) {
  const [selectedGenerationId, setSelectedGenerationId] = useState<string | undefined>();
  const readyWorks = history.filter((item) => item.status === "succeeded" && item.result !== undefined);
  const selectedIndex = readyWorks.findIndex((item) => item.generationId === selectedGenerationId);
  const selected = selectedIndex < 0 ? undefined : readyWorks[selectedIndex];
  const templateNames = new Map(templates.map((template) => [template.templateId, template.name]));
  const titleFor = (item: GenerationResponse, index: number) =>
    templateNames.get(item.templateId) ?? `Образ ${history.length - index}`;

  useEffect(() => {
    if (selected === undefined) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedGenerationId(undefined);
      if (event.key === "ArrowLeft" && readyWorks.length > 1) {
        setSelectedGenerationId(readyWorks[(selectedIndex - 1 + readyWorks.length) % readyWorks.length]!.generationId);
      }
      if (event.key === "ArrowRight" && readyWorks.length > 1) {
        setSelectedGenerationId(readyWorks[(selectedIndex + 1) % readyWorks.length]!.generationId);
      }
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [readyWorks, selected, selectedIndex]);

  return (
    <main className="screen section-screen works-screen">
      <header className="section-header works-header"><Brand /><span className="eyebrow">Личная галерея</span><h1>Мои работы</h1><p>Ваша коллекция образов — откройте любой результат и сохраните его в полном качестве.</p></header>
      <section className="works-grid" aria-live="polite">
        {history.length === 0 ? <PageState action="Выбрать образ" copy="Запущенные и готовые образы появятся здесь." onAction={() => onNavigate("catalog")} title="Пока нет работ" /> : history.map((item, index) => (
          <article className={`work-tile work-tile--${item.status}`} key={item.generationId}>
            {item.status === "succeeded" ? (
              <button aria-label={`Открыть работу «${titleFor(item, index)}»`} className="work-tile__open" onClick={() => setSelectedGenerationId(item.generationId)} type="button">
                {previewUrls[item.generationId] === undefined ? <span className="work-tile__placeholder"><Icon name="image" /></span> : <img alt={`Результат «${titleFor(item, index)}»`} src={previewUrls[item.generationId]} />}
              </button>
            ) : (
              <div className="work-tile__pending">
                <span><Icon name={item.status === "failed" ? "image" : "spark"} /></span>
                <strong>{item.status === "running" ? "Создаём образ" : "Не получилось"}</strong>
                <small>{item.status === "running" ? "Он появится здесь после обработки" : "Кредиты возвращены"}</small>
              </div>
            )}
          </article>
        ))}
      </section>
      {selected !== undefined ? <WorkViewer
        imageUrl={previewUrls[selected.generationId]}
        item={selected}
        onClose={() => setSelectedGenerationId(undefined)}
        onNext={() => setSelectedGenerationId(readyWorks[(selectedIndex + 1) % readyWorks.length]!.generationId)}
        onPrevious={() => setSelectedGenerationId(readyWorks[(selectedIndex - 1 + readyWorks.length) % readyWorks.length]!.generationId)}
        onSave={() => onSave(selected)}
        position={selectedIndex + 1}
        title={templateNames.get(selected.templateId) ?? "Ваш образ"}
        total={readyWorks.length}
      /> : null}
      <BottomNavigation active="works" onNavigate={onNavigate} />
    </main>
  );
}

export function WorkViewer({
  item,
  imageUrl,
  title,
  position,
  total,
  onClose,
  onPrevious,
  onNext,
  onSave,
}: {
  item: GenerationResponse;
  imageUrl?: string;
  title: string;
  position: number;
  total: number;
  onClose(): void;
  onPrevious(): void;
  onNext(): void;
  onSave(): void;
}) {
  return (
    <section aria-label={`Просмотр работы «${title}»`} aria-modal="true" className="work-viewer" role="dialog">
      <header className="work-viewer__header">
        <button aria-label="Закрыть просмотр" className="work-viewer__icon" onClick={onClose} type="button"><Icon name="close" /></button>
        <div><strong>{title}</strong><span>{position} из {total}</span></div>
        <span className="work-viewer__spacer" />
      </header>
      <div className="work-viewer__stage">
        {imageUrl === undefined ? <span className="work-viewer__loading"><Icon name="image" size={34} />Загружаем результат</span> : <img alt={`Работа «${title}»`} src={imageUrl} />}
        {total > 1 ? <><button aria-label="Предыдущая работа" className="work-viewer__arrow work-viewer__arrow--previous" onClick={onPrevious} type="button"><Icon name="arrow-left" /></button><button aria-label="Следующая работа" className="work-viewer__arrow work-viewer__arrow--next" onClick={onNext} type="button"><Icon name="arrow-right" /></button></> : null}
      </div>
      <footer className="work-viewer__footer">
        <Button disabled={item.result === undefined} onClick={onSave} tail={<Icon name="download" size={20} />}>Скачать изображение</Button>
        <p>Сохраним оригинал без обрезки и водяных знаков.</p>
      </footer>
    </section>
  );
}

export function ProfileScreen({
  balance,
  theme,
  onNavigate,
  onOpenPayments,
  onTheme,
}: {
  balance?: number;
  theme: "light" | "dark";
  onNavigate(section: PrimarySection): void;
  onOpenPayments(): void;
  onTheme(theme: "light" | "dark"): void;
}) {
  return (
    <main className="screen section-screen profile-screen">
      <header className="section-header"><Brand /><span className="eyebrow">Настройки</span><h1>Профиль</h1></header>
      <section className="balance-card"><div><span>Доступно</span><strong>{balance ?? "—"} {balance === undefined ? "кредитов" : creditWord(balance)}</strong></div><Button onClick={onOpenPayments}>Пополнить</Button></section>
      <section className="profile-section"><h2>Оформление</h2><div className="theme-switch"><button aria-pressed={theme === "light"} className={theme === "light" ? "theme-switch__active" : ""} onClick={() => onTheme("light")} type="button">Светлая</button><button aria-pressed={theme === "dark"} className={theme === "dark" ? "theme-switch__active" : ""} onClick={() => onTheme("dark")} type="button">Тёмная</button></div></section>
      <section className="profile-list">
        <article className="profile-row"><span><Icon name="lock" /></span><div><h2>О фотографиях</h2><p>Исходники хранятся до 30 дней, результаты — до 180 дней.</p><button className="text-action" type="button">Приватность и условия</button></div></article>
        <article className="profile-row"><span><Icon name="works" /></span><div><h2>Помощь</h2><p>Если возникла проблема с оплатой или результатом, напишите в поддержку.</p></div></article>
      </section>
      <BottomNavigation active="profile" onNavigate={onNavigate} />
    </main>
  );
}

export function PaymentsScreen({
  packages,
  methods,
  loading,
  busy,
  onBack,
  onRetry,
  onBuy,
}: {
  packages: CreditPackageResponse[];
  methods: PaymentMethod[];
  loading: boolean;
  busy: boolean;
  onBack(): void;
  onRetry(): void;
  onBuy(code: CreditPackageResponse["code"], method: PaymentMethod): void;
}) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | undefined>(methods[0]);
  useEffect(() => {
    if (selectedMethod === undefined || !methods.includes(selectedMethod)) {
      setSelectedMethod(methods[0]);
    }
  }, [methods, selectedMethod]);

  const methodLabel = selectedMethod === "telegram_stars" ? "Telegram Stars" : "Карта или СБП";
  const priceFor = (offer: CreditPackageResponse) => selectedMethod === "telegram_stars"
    ? `${offer.stars} ⭐`
    : `${(offer.rublesMinor / 100).toLocaleString("ru-RU")} ₽`;

  return (
    <main className="screen payments-screen">
      <ScreenHeader onBack={onBack} title="Пополнить баланс" />
      <section className="payments-title"><span className="eyebrow">Кредиты Nura</span><h1>Пополните баланс</h1><p>Сначала выберите удобный способ оплаты, затем пакет кредитов.</p></section>
      {loading ? <div className="catalog-skeleton"><span /><span /></div> : packages.length === 0 ? <PageState action="Повторить" copy="Пакеты кредитов временно недоступны." onAction={onRetry} title="Не удалось загрузить пакеты" /> : methods.length === 0 || selectedMethod === undefined ? <PageState action="Повторить" copy="Администратор временно отключил все способы оплаты." onAction={onRetry} title="Оплата на паузе" /> : (
        <section className="package-list">
          <section className="payment-method-picker" aria-labelledby="payment-method-heading">
            <div className="payment-method-picker__heading"><span className="eyebrow">Способ оплаты</span><h2 id="payment-method-heading">Как вам удобнее?</h2></div>
            <div className="payment-method-options" role="radiogroup" aria-label="Способ оплаты">
              {methods.includes("telegram_stars") ? <button aria-checked={selectedMethod === "telegram_stars"} className={selectedMethod === "telegram_stars" ? "payment-method-option payment-method-option--selected" : "payment-method-option"} disabled={busy} onClick={() => setSelectedMethod("telegram_stars")} role="radio" type="button"><span className="payment-method-option__mark" aria-hidden="true">★</span><span><strong>Telegram Stars</strong><small>Внутри Telegram</small></span><i aria-hidden="true" /></button> : null}
              {methods.includes("yookassa") ? <button aria-checked={selectedMethod === "yookassa"} className={selectedMethod === "yookassa" ? "payment-method-option payment-method-option--selected" : "payment-method-option"} disabled={busy} onClick={() => setSelectedMethod("yookassa")} role="radio" type="button"><span className="payment-method-option__mark" aria-hidden="true">₽</span><span><strong>Карта или СБП</strong><small>Безопасно через ЮKassa</small></span><i aria-hidden="true" /></button> : null}
            </div>
          </section>
          <div className="package-list__heading"><span className="eyebrow">Пакеты</span><p>Оплата: <strong>{methodLabel}</strong></p></div>
          <div className="package-list__surface">{packages.map((offer) => <article className={offer.code === "popular" ? "package-card package-card--popular" : "package-card"} key={offer.code}><div className="package-card__head"><span>{offer.label}</span><h2>{offer.credits} {creditWord(offer.credits)}</h2></div><div className="package-card__buy"><strong>{priceFor(offer)}</strong><Button className="package-action" disabled={busy} onClick={() => onBuy(offer.code, selectedMethod)}>Выбрать</Button></div></article>)}</div>
          <p className="payment-note"><Icon name="check" size={18} />Кредиты зачисляются после подтверждения оплаты</p>
        </section>
      )}
    </main>
  );
}
